import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { Spinner } from "@/components/ui/Spinner";

type Role = "operator";

/**
 * R05 — Role-aware route guard.
 * Without `requireRole`, behaves as before: gate on authentication.
 * With `requireRole`, additionally verifies the user's ID token carries
 * the named custom claim. Users missing the claim are redirected home.
 */
export function ProtectedRoute({
  children,
  requireRole,
}: {
  children: ReactNode;
  requireRole?: Role;
}) {
  const { user, loading } = useAuthContext();
  const location = useLocation();
  const [claimState, setClaimState] = useState<"loading" | "granted" | "denied">(
    requireRole ? "loading" : "granted"
  );

  useEffect(() => {
    if (!requireRole) {
      setClaimState("granted");
      return;
    }

    if (!user) {
      setClaimState("loading");
      return;
    }

    setClaimState("loading");
    let cancelled = false;
    void user.getIdTokenResult().then((result) => {
      if (cancelled) return;
      const hasRole = result.claims[requireRole] === true;
      setClaimState(hasRole ? "granted" : "denied");
    }).catch(() => {
      if (!cancelled) setClaimState("denied");
    });
    return () => { cancelled = true; };
  }, [user, requireRole]);

  if (loading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireRole && claimState === "loading") {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (requireRole && claimState === "denied") {
    // Role missing — send them to their own dashboard with a notice.
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
